/**
 * Approvals — a cashier asks a manager (who may be away) to approve a return.
 * Backend: /approvals (approvals.routes.js).
 */
import api from "./api";
import type { CreateReturnInput, RefundMethod, ReturnDisposition, ReturnReason, SalesReturn } from "./orderService";

export type ApprovalStatus = "pending" | "processing" | "approved" | "rejected" | "cancelled" | "expired" | "failed";

export interface ApprovalRequest {
  _id: string;
  kind: "sales_return" | "credit_sale";
  status: ApprovalStatus;
  businessId: string;
  requestedBy: string;
  requestedByName: string;
  summary: {
    businessName?: string;
    invoiceNumber: string;
    customerName: string;
    lines: { name: string; quantity: number; amountMinor: number; disposition?: ReturnDisposition }[];
    totalMinor: number;
    arOffsetMinor: number;
    refundMinor: number;
    refundMethod: RefundMethod | null;
    reason: ReturnReason;
    reasonLabel: string;
    note?: string;
    // credit_sale
    paidMinor?: number;
    balanceMinor?: number;
    outstandingMinor?: number;
    limitMinor?: number;
    wouldOweMinor?: number;
  };
  decidedByName?: string;
  decidedAt?: string | null;
  decisionNote?: string;
  failureReason?: string;
  resultId?: string | null;
  /** approved: the recorded return (sales_return) or sale (credit_sale) */
  result?: SalesReturn & { approvedByName?: string } & { invoiceNumber: string };
  expiresAt: string;
  createdAt: string;
}

type Wrapped<T> = { data: T };

const approvalService = {
  async requestReturn(orderId: string, input: Omit<CreateReturnInput, "managerPin" | "idempotencyKey">) {
    const res = await api.post<Wrapped<ApprovalRequest>>("/approvals/returns", { orderId, ...input });
    return res.data.data;
  },
  async requestCreditSale(order: Record<string, unknown>, idempotencyKey?: string) {
    const res = await api.post<Wrapped<ApprovalRequest>>("/approvals/credit-sales", { order, idempotencyKey });
    return res.data.data;
  },
  async get(id: string) {
    return (await api.get<Wrapped<ApprovalRequest>>(`/approvals/${id}`)).data.data;
  },
  async mine() {
    return (await api.get<Wrapped<ApprovalRequest[]>>("/approvals/mine")).data.data;
  },
  async pending() {
    return (await api.get<Wrapped<ApprovalRequest[]>>("/approvals/pending")).data.data;
  },
  async approve(id: string, pin: string) {
    return (await api.post<Wrapped<{ request: ApprovalRequest; salesReturn: SalesReturn }>>(`/approvals/${id}/approve`, { pin })).data.data;
  },
  async reject(id: string, note?: string) {
    return (await api.post<Wrapped<ApprovalRequest>>(`/approvals/${id}/reject`, { note })).data.data;
  },
  async cancel(id: string) {
    return (await api.post<Wrapped<ApprovalRequest>>(`/approvals/${id}/cancel`)).data.data;
  },
};

export default approvalService;
